import React from 'react';
import BubbleCommon, { engagementTipsText } from '@op/components/BubbleCommon';
import ProgressBox from './components/ProgressBox';
import VideoBox from './components/VideoBox';
import moment from 'moment';
import { transLanguage, transRegionText } from '@op/utils';
import ReviewBox from '@op/components/ReviewBox';
import { keywordRenderFunc } from '@op/utils/text';
import ChannelLabel, { channelInfo } from '@op/components/ChannelLabel';
import { DBO_V } from '@op/utils/format';
import { getCountryFlag } from '@home/_config/countries';
import RenderImg from '@op/components/RenderImg';

export type keys = 'video' | 'view' | 'follower' | 'engagement';

export const videoColumnsFunc = ({
  formatMessage,
  language,
  renderKey,
  statistics,
  navigatePage,
  logBtnId,
  logCommentMap,
  logLoading,
  gameInfo,
  gameMap,
}: any) => {
  const isString = typeof renderKey === 'string';

  function getContentInfo(
    content: string,
    renderKey: string | Array<string>,
    sentimentStr?: string,
  ) {
    return keywordRenderFunc({
      text: content,
      keyword: renderKey,
      sentimentInfo: sentimentStr,
      titleCase: true,
    });
  }

  function getUpdateFilter(cvm: any) {
    return {
      unified_id: cvm.unified_id,
      edition_id: cvm.edition_id,
      comment_time: moment(cvm.comment_time).format('YYYY-MM-DD HH:mm:ss'),
      comment_uin: cvm.comment_uin,
      comment_create_time: moment(cvm.create_time).format(
        'YYYY-MM-DD HH:mm:ss',
      ),
    };
  }

  return {
    video: {
      key: 'comment_time',
      header: formatMessage({ id: 'Video (Sort by Date)' }),
      width: 530,
      render: (records: any) => (
        <VideoBox
          formatMessage={formatMessage}
          language={language}
          keyword={renderKey}
          records={records}
          navigatePage={navigatePage}
          logBtnId={logBtnId}
          gameInfo={gameInfo}
          gameMap={gameMap}
        />
      ),
      fixed: 'left',
    },
    view: {
      key: 'view',
      header: formatMessage({ id: 'View' }),
      width: 190,
      render: (cvm: any) => {
        const value = Number(cvm.view);
        return (
          <ProgressBox
            count={value >= 0 ? value : 0}
            total_count={statistics?.max_views || 0}
          />
        );
      },
    },
    engagement: {
      key: 'engagement',
      header: (
        <div className="dbo-tablecommon-header">
          <span> {formatMessage({ id: 'Engagement' })} </span>
          <BubbleCommon tipsMap={engagementTipsText}></BubbleCommon>
        </div>
      ),
      width: 190,
      render: (cvm: any) => (
        <ProgressBox
          count={Number(cvm.engagement)}
          total_count={statistics?.max_engagements || 0}
        />
      ),
    },
    subscribe: {
      key: 'follower_number',
      header: formatMessage({ id: 'Followers' }),
      width: 190,
      render: (cvm: any) => (
        <ProgressBox
          count={Number(cvm.subscribe)}
          total_count={statistics?.max_followers || 0}
        />
      ),
    },
    channelName: {
      key: 'channel_name',
      header: formatMessage({ id: 'Channel' }),
      width: 160,
      render: (cvm: any) => <ChannelLabel channel_name={cvm.channel_name} />,
    },
    game: {
      key: 'unified_id',
      header: formatMessage({ id: 'Game' }),
      width: 180,
      fixed: 'left',
      render: (cvm: any) => {
        const matched = gameMap?.get(cvm.unified_id);
        return (
          <div className="dbo-tablecommon-game">
            <RenderImg
              coverImage={matched?.cover}
              className="dbo-tablecommon-game__icon"
            />
            <span className="dbo-tablecommon-game__name">
              {matched?.name || '-'}
            </span>
          </div>
        );
      },
    },
    post: {
      key: 'post',
      header: formatMessage({ id: 'Posts' }),
      width: 520,
      fixed: 'left',
      render: (cvm: any) => {
        const { content, content_to_en, content_to_zh } = cvm;
        let contentInfo = content;
        if (language === 'en') {
          contentInfo = content_to_en;
        } else if (language === 'zh') {
          contentInfo = content_to_zh;
        }

        const channelIcon = channelInfo(cvm.channel_name);
        const usernameNode = gameMap ? (
          <div className="dbo-tablecommon-username-with-channel">
            <img src={channelIcon.img} className="dbo-tablecommon-username-channel-icon" />
            <span className="dbo-tablecommon-username-channel-text">{cvm.reviewer}</span>
          </div>
        ) : cvm.reviewer;

        return (
          <ReviewBox
            // 源语言评论不支持编辑
            editable={false}
            data={cvm}
            link={cvm.content_url}
            columnsIsFirst
            sentiment={cvm.sentiment_rating}
            editTranslate={contentInfo}
            content={getContentInfo(
              contentInfo,
              isString ? renderKey : renderKey?.origin,
            )}
            originContent={contentInfo}
            filter={getUpdateFilter(cvm)}
            language={language}
            username={usernameNode}
            logBtnId={logBtnId}
            logCommentMap={logCommentMap}
            logLoading={logLoading}
            gameInfo={gameMap ? (gameMap.has(cvm.unified_id) ? { edition_unified_id: cvm.unified_id } : undefined) : gameInfo}
          ></ReviewBox>
        );
      },
    },
    language: {
      key: 'language',
      header: formatMessage({ id: 'Language' }),
      align: 'left',
      width: 110,
      render: (cvm: any) => transLanguage(cvm?.language),
    },
    commentTime: {
      key: 'comment_time',
      header: formatMessage({ id: 'Time' }),
      align: 'left',
      width: 165,
      render: (cvm: any) => moment(cvm.comment_time).format('YYYY-MM-DD HH:mm'),
    },
    tweetsReply: {
      key: 'tweets_reply',
      header: formatMessage({ id: 'Comments' }),
      width: 120,
      align: 'center',
      render: (cvm: any) => {
        if (Number(cvm.tweets_reply) < 1) {
          return '-';
        }
        if ('0' == cvm.tweets_reply) {
          return '0';
        }
        return DBO_V.reviews(cvm.tweets_reply);
      },
    },
    authorCountry: {
      key: 'author_country',
      header: formatMessage({ id: 'KOL Region/Country' }),
      align: 'left',
      width: 190,
      render: (cvm: any) => (
        <div className="dbo-tablecommon-country">
          {cvm?.author_country ? (
            <>
              <img
                src={getCountryFlag(cvm?.author_country)}
                className="dbc-region-select-item-icon  "
              />
              <span>{transRegionText(cvm?.author_country)}</span>
            </>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
  };
};
